import { Fragment, useEffect, useRef, useState } from 'react';
import { Alert, Button, Collapse, Divider, Flex, Input, Popconfirm, Spin, Tag, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined, SendOutlined } from '@ant-design/icons';
import { useAskAiMutation, useGetAiStatusQuery } from '@/entities/ai-assistant';
import type { AiCall, AiChoice, AiReplay, AiSuggestion } from '@/entities/ai-assistant';
import { useGetMeQuery } from '@/entities/user';
import { translateApiError } from '@/shared/api/errorToMessage';
import { useBreakpoint } from '@/shared/lib/hooks';
import { useStoreContext } from '@/shared/lib/store-context';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { AiAnswerTable } from './AiAnswerTable';
import { clearHistory, contextOf, loadHistory, saveHistory, type ChatEntry } from './chatHistory';

export interface AiAssistantWindowProps {
  onClose: () => void;
}

const MAX_QUESTION_LENGTH = 1000;

const SUGGESTIONS = [
  'Сколько я сегодня заработал?',
  'Кто мне должен?',
  'Топ-10 товаров по прибыли за месяц',
  'Что в минусе по остатку?',
  'Сколько стоит склад?',
  'Что скоро испортится?',
];

const newId = (): string => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

function describeArgs(args: Record<string, unknown>): string {
  const parts = Object.entries(args).map(([key, value]) => `${key}: ${typeof value === 'string' ? value : JSON.stringify(value)}`);
  return parts.length > 0 ? parts.join(', ') : 'без параметров';
}

function Bubble({ side, children }: { side: 'user' | 'assistant'; children: React.ReactNode }) {
  const { isMobile } = useBreakpoint();
  return (
    <Flex justify={side === 'user' ? 'flex-end' : 'flex-start'}>
      <div
        style={{
          maxWidth: isMobile ? '94%' : '85%',
          minWidth: 0,
          padding: '6px 8px',
          fontSize: isMobile ? 14 : 12,
          background: side === 'user' ? '#e6f4ff' : '#f5f5f5',
          border: '1px solid #d9d9d9',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
        }}
      >
        {children}
      </div>
    </Flex>
  );
}

/** «Откуда данные»: какие инструменты вызвал ассистент и с какими параметрами — чтобы ответ можно было перепроверить. */
function Sources({ calls }: { calls: AiCall[] }) {
  if (calls.length === 0) return null;
  return (
    <Collapse
      size="small"
      ghost
      items={[
        {
          key: 'sources',
          label: <span style={{ fontSize: 12 }}>Откуда данные</span>,
          children: (
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12 }}>
              {calls.map((call, index) => (
                <li key={`${call.tool}-${index}`}>
                  {call.label}: {describeArgs(call.args)}
                  {call.error ? <Typography.Text type="danger"> — ошибка вызова</Typography.Text> : null}
                </li>
              ))}
            </ul>
          ),
        },
      ]}
    />
  );
}

interface AnswerProps {
  entry: ChatEntry;
  /** Варианты и кнопки следующего шага живы только под последним ответом и пока ассистент не занят. */
  interactive: boolean;
  onSuggestion: (suggestion: AiSuggestion) => void;
  onChoice: (choice: AiChoice, optionId: string) => void;
  /** Сервер разрешает «Разобрать подробнее»: тогда под ответом есть кнопка с явным согласием. */
  detailedAvailable: boolean;
  onDetailed: () => void;
}

function Answer({ entry, interactive, onSuggestion, onChoice, detailedAvailable, onDetailed }: AnswerProps) {
  const tables = entry.calls.filter((call) => call.table && call.table.rows.length > 0);
  const choices = interactive ? (entry.choices ?? []) : [];
  const suggestions = interactive ? (entry.suggestions ?? []) : [];
  return (
    <Flex vertical gap={6}>
      {entry.detailed ? (
        <div>
          <Tag color="gold" style={{ marginInlineEnd: 0 }}>
            ИИ видел данные магазина
          </Tag>
        </div>
      ) : null}
      <Bubble side="assistant">{entry.reply || 'Ассистент не вернул текст ответа.'}</Bubble>
      {choices.map((choice) => (
        <Flex key={`${choice.entity}-${choice.typed}`} vertical gap={4}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Выберите, кого вы имели в виду:
          </Typography.Text>
          <Flex gap={6} wrap>
            {choice.options.map((option) => (
              <Button key={option.id} size="small" onClick={() => onChoice(choice, option.id)}>
                {option.label}
              </Button>
            ))}
          </Flex>
        </Flex>
      ))}
      {entry.warnings.map((warning) => (
        <Alert key={warning} type="warning" showIcon message={warning} style={{ fontSize: 12 }} />
      ))}
      {tables.map((call, index) => (
        <AiAnswerTable key={`${call.tool}-${index}`} table={call.table!} />
      ))}
      <Sources calls={entry.calls} />
      {suggestions.length > 0 ? (
        <Flex gap={6} wrap aria-label="Что дальше">
          {suggestions.map((suggestion) => (
            <Button key={suggestion.label} size="small" onClick={() => onSuggestion(suggestion)}>
              {suggestion.label}
            </Button>
          ))}
        </Flex>
      ) : null}
      {interactive && detailedAvailable && !entry.detailed ? (
        <div>
          <Popconfirm
            title="Отправить вопрос вместе с данными магазина?"
            description="ИИ увидит суммы, названия контрагентов и товаров из отчётов — это нужно, чтобы он мог рассуждать и сравнивать. Остальные вопросы по-прежнему идут без данных."
            okText="Отправить с данными"
            cancelText="Отмена"
            onConfirm={onDetailed}
          >
            <Button size="small" type="dashed">
              Разобрать подробнее
            </Button>
          </Popconfirm>
        </div>
      ) : null}
    </Flex>
  );
}

/**
 * ИИ-ассистент для отчётов: владелец спрашивает словами («сколько я сегодня заработал?»), ассистент
 * читает данные магазина готовыми инструментами и отвечает текстом + точной таблицей. Ничего не
 * меняет. Беседа хранится в браузере (по магазину и пользователю), на сервер уходят только последние
 * реплики текущего диалога для контекста.
 */
export function AiAssistantWindow({ onClose }: AiAssistantWindowProps) {
  const { isMobile } = useBreakpoint();
  const { storeId } = useStoreContext();
  const { data: me } = useGetMeQuery();
  const userId = me?.id;
  const { data: status } = useGetAiStatusQuery();
  const [ask, { isLoading }] = useAskAiMutation();

  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [loadedFor, setLoadedFor] = useState<string | undefined>(undefined);
  const [sessionId, setSessionId] = useState(newId);
  const [draft, setDraft] = useState('');
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // История — из браузера, как только известен пользователь (подстройка состояния при смене пропса, не эффект).
  const historyOwner = userId ? `${storeId}:${userId}` : undefined;
  if (historyOwner && loadedFor !== historyOwner && userId) {
    setLoadedFor(historyOwner);
    setEntries(loadHistory(storeId, userId));
  }

  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ block: 'end' });
  }, [entries.length, pendingQuestion, errorText]);

  const disabled = status?.enabled === false;
  const limitReached = status !== undefined && status.usedToday >= status.dailyLimit;

  async function send(rawText: string, replay?: AiReplay, detailed = false) {
    const text = rawText.trim();
    if (!text || isLoading || disabled) return;
    setErrorText(null);
    setPendingQuestion(text);
    setDraft('');
    try {
      const response = await ask({ text, history: contextOf(entries, sessionId), ...(replay ? { replay } : {}), ...(detailed ? { detailed: true } : {}) }).unwrap();
      const entry: ChatEntry = {
        id: newId(),
        sessionId,
        question: text,
        reply: response.reply,
        answered: response.answered,
        calls: response.calls,
        warnings: response.warnings,
        ...(response.plan && response.plan.length > 0 ? { plan: response.plan } : {}),
        ...(response.choices && response.choices.length > 0 ? { choices: response.choices } : {}),
        ...(response.suggestions && response.suggestions.length > 0 ? { suggestions: response.suggestions } : {}),
        ...(response.detailed ? { detailed: true } : {}),
        at: new Date().toISOString(),
      };
      const next = [...entries, entry];
      setEntries(next);
      if (userId) saveHistory(storeId, userId, next);
    } catch (error) {
      // Вопрос не потерян: возвращаем его в поле ввода, чтобы можно было отправить ещё раз.
      setErrorText(translateApiError(error));
      setDraft(text);
    } finally {
      setPendingQuestion(null);
    }
  }

  /** Кнопка-подсказка: сервер выполняет готовый план, модель не вызывается. */
  function handleSuggestion(suggestion: AiSuggestion) {
    void send(suggestion.question, { plan: suggestion.plan });
  }

  /** Выбор из вариантов: тот же вопрос, но с конкретным контрагентом (товаром, документом), без модели. */
  function handleChoice(entry: ChatEntry, choice: AiChoice, optionId: string) {
    void send(entry.question, { plan: choice.plan, picks: [{ entity: choice.entity, typed: choice.typed, id: optionId }] });
  }

  /** «Разобрать подробнее»: тот же вопрос, но с согласием отправить данные отчётов модели; ответ помечается. */
  function handleDetailed(entry: ChatEntry) {
    void send(entry.question, undefined, true);
  }

  function handleClearHistory() {
    if (userId) clearHistory(storeId, userId);
    setEntries([]);
    setSessionId(newId());
    setErrorText(null);
  }

  const lastEntry = entries[entries.length - 1];
  const showNewDialogDivider = lastEntry !== undefined && lastEntry.sessionId !== sessionId;
  const counter = status ? `Сегодня задано ${status.usedToday} из ${status.dailyLimit}` : null;

  const footer = (
    <Flex vertical gap={6}>
      <Flex gap={8} align="flex-end">
        <Input.TextArea
          // На телефоне без автофокуса: клавиатура не должна выскакивать при открытии окна.
          {...(isMobile ? {} : { 'data-autofocus': true })}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onPressEnter={(event) => {
            if (event.shiftKey) return;
            event.preventDefault();
            void send(draft);
          }}
          autoSize={{ minRows: 1, maxRows: 4 }}
          maxLength={MAX_QUESTION_LENGTH}
          placeholder="Спросите про прибыль, долги, остатки…"
          disabled={disabled || limitReached}
          aria-label="Вопрос ассистенту"
        />
        <Button type="primary" icon={<SendOutlined />} loading={isLoading} disabled={!draft.trim() || disabled || limitReached} onClick={() => void send(draft)}>
          {isMobile ? null : 'Спросить'}
        </Button>
      </Flex>
      <Flex justify="space-between" align="center" gap={8} wrap>
        <Flex gap={4}>
          <Button size="small" type="link" icon={<PlusOutlined />} disabled={entries.length === 0 || isLoading} onClick={() => setSessionId(newId())}>
            Новый диалог
          </Button>
          <Popconfirm title="Очистить историю на этом устройстве?" okText="Очистить" cancelText="Отмена" onConfirm={handleClearHistory}>
            <Button size="small" type="link" danger icon={<DeleteOutlined />} disabled={entries.length === 0 || isLoading}>
              Очистить историю
            </Button>
          </Popconfirm>
        </Flex>
        {counter ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {counter}
          </Typography.Text>
        ) : null}
      </Flex>
    </Flex>
  );

  return (
    <FloatingWindow kind="aiAssistant" title="ИИ-ассистент" onClose={onClose} footer={footer} initialFocus={isMobile ? 'window' : 'auto'}>
      <Flex vertical gap={10}>
        {disabled ? <Alert type="info" showIcon message="Ассистент не включён для этого магазина." /> : null}
        {limitReached ? <Alert type="warning" showIcon message="Дневной лимит вопросов исчерпан — попробуйте завтра." /> : null}

        {entries.length === 0 && !pendingQuestion && !disabled ? (
          <Flex vertical gap={8}>
            <Typography.Text type="secondary" style={{ fontSize: isMobile ? 14 : 12 }}>
              Задайте вопрос по данным магазина — ассистент только читает, ничего не меняет.
            </Typography.Text>
            <Flex gap={6} wrap>
              {SUGGESTIONS.map((suggestion) => (
                <Button key={suggestion} size="small" onClick={() => void send(suggestion)} disabled={limitReached}>
                  {suggestion}
                </Button>
              ))}
            </Flex>
          </Flex>
        ) : null}

        {entries.map((entry, index) => (
          <Fragment key={entry.id}>
            {index > 0 && entries[index - 1]!.sessionId !== entry.sessionId ? <Divider plain style={{ margin: '4px 0', fontSize: 12 }}>Новый диалог</Divider> : null}
            <Bubble side="user">{entry.question}</Bubble>
            <Answer
              entry={entry}
              interactive={index === entries.length - 1 && !isLoading && !disabled}
              onSuggestion={handleSuggestion}
              onChoice={(choice, optionId) => handleChoice(entry, choice, optionId)}
              detailedAvailable={status?.detailedAvailable === true}
              onDetailed={() => handleDetailed(entry)}
            />
          </Fragment>
        ))}

        {showNewDialogDivider ? (
          <Divider plain style={{ margin: '4px 0', fontSize: 12 }}>
            Новый диалог — контекст сброшен
          </Divider>
        ) : null}

        {pendingQuestion ? (
          <>
            <Bubble side="user">{pendingQuestion}</Bubble>
            <Flex align="center" gap={8} style={{ fontSize: isMobile ? 14 : 12, color: '#8c8c8c' }}>
              <Spin size="small" /> Думаю…
            </Flex>
          </>
        ) : null}

        {errorText ? <Alert type="error" showIcon message={errorText} closable onClose={() => setErrorText(null)} /> : null}
        <div ref={bottomRef} />
      </Flex>
    </FloatingWindow>
  );
}
